import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  Paper,
  TextField,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { companyInfoData } from "../services/apiService";
import { REACT_APP_SITE_URL, SITE_NAME } from "../config/seo";
import {
  generateWebPageStructuredData,
  generateBreadcrumbStructuredData,
} from "../utils/structuredData";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GradientHero,
  GradientText,
  GlassCard,
  GoogleRatingBadge,
} from "../components/ui";
import { useGoogleRating } from "../hooks/useGoogleRating";

const PAGE_DESCRIPTION =
  "Get in touch with R S Enterprises for ECR machine and POS system inquiries, pricing, and support. Located in Laxmi Nagar, New Delhi.";

interface FormData {
  name: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  // Static data — synchronous for prerender/hydration parity
  const companyInfo = companyInfoData;
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { hash } = useLocation();
  const { data: googleRating } = useGoogleRating();

  // Arriving via /contact#contact-form (e.g. the Home hero CTA): scroll to the
  // form and focus its first field. Delayed a tick so the entrance animations
  // and layout have settled before measuring scroll position.
  useEffect(() => {
    if (hash !== "#contact-form") return;
    const timer = window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      nameInputRef.current?.focus({ preventScroll: true });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const data = new FormData(formRef.current);
    const values = {
      name: data.get("name") as string,
      phone: data.get("phone") as string,
      message: data.get("message") as string,
    };

    if (!values.name.trim() || !values.phone.trim() || !values.message.trim()) {
      setSubmitStatus({ success: false, message: "Please fill in all required fields." });
      return;
    }
    if (values.name.trim().length < 2) {
      setSubmitStatus({ success: false, message: "Name must be at least 2 characters." });
      return;
    }
    if (!/^\+?[\d\s-]{7,15}$/.test(values.phone.trim())) {
      setSubmitStatus({ success: false, message: "Please enter a valid phone number." });
      return;
    }
    if (values.message.trim().length < 10) {
      setSubmitStatus({ success: false, message: "Message must be at least 10 characters." });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (
        !process.env.REACT_APP_EMAIL_SERVICE_ID ||
        !process.env.REACT_APP_EMAIL_TEMPLATE_ID ||
        !process.env.REACT_APP_EMAIL_PUBLIC_KEY
      ) {
        throw new Error(
          "Email configuration is missing. Please check your environment variables.",
        );
      }

      await emailjs.send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_TEMPLATE_ID,
        values,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY,
      );

      setSubmitStatus({
        success: true,
        message: "Thank you for your message! We will get back to you soon.",
      });
      setFormData({ name: "", phone: "", message: "" });
    } catch (error) {
      console.error("Failed to send email:", error);
      setSubmitStatus({
        success: false,
        message: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (name: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const pageUrl = `${REACT_APP_SITE_URL}/contact`;

  const contactInfo = companyInfo
    ? [
        {
          icon: <LocationOnIcon sx={{ color: "secondary.main" }} fontSize="large" />,
          title: "Our Location",
          description: companyInfo.contact.address,
        },
        {
          icon: <EmailIcon sx={{ color: "secondary.main" }} fontSize="large" />,
          title: "Email Us",
          description: companyInfo.contact.email,
        },
        {
          icon: <PhoneIcon sx={{ color: "secondary.main" }} fontSize="large" />,
          title: "Call Us",
          description: companyInfo.contact.phone,
        },
        {
          icon: <AccessTimeIcon sx={{ color: "secondary.main" }} fontSize="large" />,
          title: "Business Hours",
          description: "Monday – Saturday\n10:00 AM – 6:00 PM",
        },
      ]
    : [];

  return (
    <>
      <SEO
        title="Contact Us"
        description={PAGE_DESCRIPTION}
        jsonLd={[
          generateWebPageStructuredData({
            title: `Contact Us | ${SITE_NAME}`,
            description: PAGE_DESCRIPTION,
            url: pageUrl,
          }),
          generateBreadcrumbStructuredData([
            { name: "Home", item: REACT_APP_SITE_URL },
            { name: "Contact", item: pageUrl },
          ]),
        ]}
      />

      <GradientHero
        compact
        eyebrow="Get In Touch"
        title={
          <>
            Let's talk about <GradientText>your business</GradientText>
          </>
        }
        subtitle="We'd love to hear from you. Get in touch with us for any questions or inquiries."
      />

      {/* Info cards */}
      <Section py={{ xs: 5, md: 6 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 3,
          }}
        >
          {contactInfo.map((item, index) => (
            <AnimatedInView key={item.title} delay={index * 0.06}>
              <GlassCard tone="light" sx={{ height: "100%", textAlign: "center" }}>
                <Box sx={{ mb: 1.5 }}>{item.icon}</Box>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                  {item.description}
                </Typography>
              </GlassCard>
            </AnimatedInView>
          ))}
        </Box>
      </Section>

      {/* Form + map */}
      <Section bg="paper" py={{ xs: 5, md: 7 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 4,
            alignItems: "stretch",
          }}
        >
          <AnimatedInView>
            <Paper
              component="form"
              id="contact-form"
              ref={formRef}
              onSubmit={handleSubmit}
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Send us a message
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Fill out the form below and we'll get back to you as soon as possible.
              </Typography>

              <TextField
                name="name"
                label="Your Name"
                inputRef={nameInputRef}
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                fullWidth
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                name="phone"
                label="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                required
                fullWidth
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                name="message"
                label="Message"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                required
                fullWidth
                margin="normal"
                multiline
                rows={4}
              />

              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<SendIcon />}
                  disabled={isSubmitting}
                  sx={{ minWidth: 200, opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
                {submitStatus && (
                  <Typography
                    variant="body2"
                    color={submitStatus.success ? "success.main" : "error.main"}
                    sx={{ mt: 2 }}
                  >
                    {submitStatus.message}
                  </Typography>
                )}
              </Box>
            </Paper>
          </AnimatedInView>

          <AnimatedInView delay={0.1}>
            {googleRating && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <GoogleRatingBadge data={googleRating} tone="light" />
              </Box>
            )}
            <Box
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: 4,
                height: "100%",
                minHeight: 420,
              }}
            >
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?q=1st%20Floor%2C%20Office%20No.%20106%2C%20107%2C%20I.J%20Complex%2C%20G-29%2C%20Near%2C%20Subhash%20Chowk%2C%20opp.%20Metro%20Pillar%20Number%2042%2C%20Laxmi%20Nagar%2C%20New%20Delhi%20-%20110092&key=${process.env.REACT_APP_MAPS_API_KEY || ""}`}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 420 }}
                allowFullScreen
                loading="lazy"
                title="Our Location"
              />
            </Box>
          </AnimatedInView>
        </Box>
      </Section>
    </>
  );
};

export default Contact;
